import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, Image, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PinRendererHealth = () => {
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const loadHealth = useCallback(async (silent = false) => {
        if (!silent) setRefreshing(true);
        try {
            const { data } = await axios.get(`${API}/admin/pin-renderer/health`, {
                withCredentials: true,
            });
            setHealth(data);
        } catch (error) {
            setHealth({ ok: false, request_failed: true });
            if (!silent) {
                toast.error(error?.response?.data?.detail || "Pin renderer health check failed");
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        loadHealth(true);
    }, [loadHealth]);

    if (loading) {
        return (
            <div className="bl-card p-5 flex items-center gap-2 text-sm text-bl-muted" data-testid="pin-renderer-health-loading">
                <Loader2 className="h-4 w-4 animate-spin" />
                Checking pin renderer…
            </div>
        );
    }

    const healthy = Boolean(health?.ok);
    const checks = [
        ["Brand icon", health?.icon_present],
        ["Wordmark font", Boolean(health?.wordmark_font_resolved)],
        ["Headline font", Boolean(health?.headline_font_resolved)],
        ["Sample render", health?.sample_render_ok],
    ];

    return (
        <div className="bl-card p-5 space-y-3" data-testid="pin-renderer-health-tile">
            <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <Image className="h-4 w-4 text-bl-teal" />
                        <h3 className="text-base font-semibold text-bl">Pin renderer</h3>
                        <span
                            className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                                healthy ? "bg-bl-green-soft text-bl-green" : "bg-bl-rust-tint text-bl-rust"
                            }`}
                            data-testid="pin-renderer-health-badge"
                        >
                            {healthy ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                            {healthy ? "Ready" : "Needs attention"}
                        </span>
                    </div>
                    <p className="text-xs text-bl-muted" data-testid="pin-renderer-health-summary">
                        {healthy
                            ? `Production-style compose passed · ${Number(health?.sample_render_size_bytes || 0).toLocaleString()} bytes`
                            : health?.request_failed
                                ? "Health endpoint could not be reached."
                                : "A required image, font, or sample compose check failed."}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => loadHealth(false)}
                    disabled={refreshing}
                    className="bl-btn-ghost p-2 disabled:opacity-50"
                    aria-label="Refresh pin renderer health"
                    title="Refresh pin renderer health"
                    data-testid="pin-renderer-health-refresh-button"
                >
                    <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
                </button>
            </div>

            <div className="grid grid-cols-2 gap-2" data-testid="pin-renderer-health-checks">
                {checks.map(([label, passed]) => (
                    <div key={label} className="flex items-center gap-2 rounded border border-bl-line px-2.5 py-2 text-xs">
                        {passed ? (
                            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-bl-green" />
                        ) : (
                            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-bl-rust" />
                        )}
                        <span className={passed ? "text-bl" : "text-bl-rust"}>{label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PinRendererHealth;